import editorialStyle from "../../../components/editorial/style.scss";
import expandableStyle from "../../../components/expandable/style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  ButtonBar,
  LargeViewOnly,
  StickyButtons,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import SlugHeading from "../../../components/slug-heading";
import Tags from "../../../components/tags";
import SlugLink from "../../../components/slug-link";
import Accordion from "../../../components/accordion";
import AccordionPreview from "../../../components/accordion/preview";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { mergeClasses } from "../../../components/helpers";
import { lookupByUrl } from "../../../settings/route-data-lookup";

import AffiliateAd from "../../../components/affiliate-ad";

let headerInfo = lookupByUrl(namedRoutes.ecoflow.dp);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionX={30}
      positionY={73}
      background="/images/products/ecoflow/delta-pro/ecoflow-delta-pro.jpg"
      titlePrefix="EcoFlow"
      title="Delta Pro"
      subTitle="Vanlife Power Station Review"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <LargeViewOnly>
          <ButtonList size="medium">
            <Button
              size="medium"
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/collections/delta-pro-series/products/delta-pro-portable-power-station"
              icon="EcoFlow"
            >
              View on EcoFlow
            </Button>
            <Button
              size="medium"
              affiliate="https://amzn.to/4oJeF0L"
              href="https://www.amazon.com/dp/B0C1Z4GLKS/"
              secondary
              icon="Amazon"
            >
              View on Amazon
            </Button>
          </ButtonList>
        </LargeViewOnly>

        <p>
          The <strong>EcoFlow Delta Pro</strong> is a powerful and versatile
          power station that set a new standard when it was first released. With
          its large capacity, robust AC output, and wide range of ports, it
          quickly became a go-to alternative to complex DIY electrical systems.
          It saves significant time and money while still delivering the
          reliability and flexibility vanlifers and off-grid users need. In this
          review, I’ll walk through what makes the Delta Pro such a strong
          option and how it compares to newer models like the Delta Pro 3.
        </p>

        <AffiliateDisclosure />

        <ImageSet
          size={5}
          border={true}
          images={[
            {
              lg: "/images/products/ecoflow/delta-pro/front.webp",
              alt: "",
            },
            {
              lg: "/images/products/ecoflow/delta-pro/right.webp",
              alt: "",
            },
            {
              lg: "/images/products/ecoflow/delta-pro/back.webp",
              alt: "",
            },
            {
              lg: "/images/products/ecoflow/delta-pro/left.webp",
              alt: "",
            },
            {
              lg: "/images/products/ecoflow/delta-pro/front-right.webp",
              alt: "",
            },
          ]}
        />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Highlights</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Detailed Comparison</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Full Specifications</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Accessories</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>DC Fuse Panel</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Alternator Chargers</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Solar Panels</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Extra Batteries</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Other Accessories</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Considerations</SlugLink>
                  </li>
                  <li>
                    <SlugLink>My Verdict</SlugLink>
                  </li>
                </ol>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Highlights</SlugHeading>

        <ul>
          <li>
            <strong>Amazing value:</strong> discounted at $1,499
          </li>
          <li>
            <strong>High capacity battery:</strong> 3,600Wh, expandable to
            10.8kWh
          </li>
          <li>
            <strong>Supports large series solar array:</strong> 11-150V⎓15A
            (1,600W)
          </li>
          <li>
            <strong>AC output:</strong> 3,600W total (7,200W surge)
          </li>
          <li>
            <strong>DC output:</strong> 12V DC Anderson Powerpole out
            (12.6V⎓30A, 378W max)
          </li>
          <li>
            <strong>Cigarette lighter port:</strong> Handy for small fridges or
            electronics
          </li>
          <li>
            <strong>6x USB ports:</strong> 4x USB A / 2x USB C
          </li>
          <li>
            <strong>Control screen support:</strong> accessory displays power
            input/output and has buttons to toggle AC/DC output
          </li>
          <li>
            <strong>EV station charging:</strong> accessory can be used to
            charge at EV stations
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">System Example</SlugHeading>
        <ImageSet
          size={1}
          images={[
            {
              lg: "/images/diagrams/power-systems/ecoflow/ecoflow-delta-pro.webp?v=2",
              alt: "EcoFlow DELTA Pro vanlife system diagram",
            },
          ]}
        />

        <AffiliateAd brand="EcoFlow" size="billboard" />
      </Center>
      <Center>
        <SlugHeading tag="h2">Detailed Comparison</SlugHeading>
        <p>
          Here’s a side‑by‑side breakdown of the Delta Pro and newer Delta Pro
          3:
        </p>

        <Fragment>
          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky}>Model</th>
                  <th>Delta Pro</th>
                  <th>Delta Pro 3</th>
                </tr>
                <tr style="font-style:italic;">
                  <th class={editorialStyle.sticky}>AKA</th>
                  <th>DP</th>
                  <th>DP3</th>
                </tr>
              </thead>
              <tbody>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Photo</strong>
                  </th>
                  <td>
                    <Image
                      src="/images/products/ecoflow/delta-pro/front-right.webp"
                      alt="EcoFlow Delta Pro"
                      showAlt
                      maxHeight={200}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/ecoflow/delta-pro-3/front-right.webp"
                      alt="EcoFlow Delta Pro 3"
                      showAlt
                      maxHeight={200}
                    />
                  </td>
                </tr>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Links</strong>
                  </th>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-portable-power-station?variant=40516140138569"
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://manuals.ecoflow.com/us/product/delta-pro-portable-power-station?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-3-portable-power-station?variant=41385721004105"
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://manuals.ecoflow.com/us/product/delta-pro-3-portable-power-station?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Price</strong>
                  </th>
                  <td>
                    <NoWrap>
                      $1,499 <s>$3,699</s>
                    </NoWrap>
                  </td>
                  <td>
                    <NoWrap>
                      $2,599 <s>$3,699</s>
                    </NoWrap>
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Capacity</strong>
                  </th>
                  <td>3,600Wh</td>
                  <td>4,096Wh</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Extra battery</strong>
                  </th>
                  <td>2x</td>
                  <td>2x</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC output</strong>
                  </th>
                  <td>
                    3,600W total
                    <br />
                    7,200W surge
                  </td>
                  <td>
                    4,000W total
                    <br />
                    8,000W surge
                  </td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC ports</strong>
                  </th>
                  <td>
                    x4 (20A each)
                    <br />
                    x1 (30A)
                  </td>
                  <td>
                    ×4 (20A each)
                    <br />
                    x1 (30A)
                    <br />
                    x1 (120/240V~16.7A)
                    <br />
                    x1 (240V~16.7A)
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>DC output</strong>
                  </th>
                  <td>12.6V⎓30A, 378W</td>
                  <td>12.6V⎓30A, 378W</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>Anderson</strong>
                  </th>
                  <td>30A, 378W</td>
                  <td>30A, 378W</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>Cigarette</strong>
                  </th>
                  <td>8A, 100W</td>
                  <td>none</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>DC5521</strong>
                  </th>
                  <td>x2 3A, 38W</td>
                  <td>5A</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>
                      <NoWrap>Solar input 1</NoWrap>
                    </strong>
                  </th>
                  <td>
                    11-150V⎓15A
                    <br />
                    1,600W
                  </td>
                  <td>
                    30-150V⎓15A
                    <br />
                    1,600W
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>
                      <NoWrap>Solar input 2</NoWrap>
                    </strong>
                  </th>
                  <td>none</td>
                  <td>
                    11V-60V⎓20A
                    <br />
                    1,000W
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>
                      Alternator input <sup>1</sup>
                    </strong>
                  </th>
                  <td>100-800W</td>
                  <td>100-800W</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC input</strong>
                  </th>
                  <td>15A 1,800W</td>
                  <td>15A 1,800W</td>
                </tr>
                <tr
                  class={mergeClasses([
                    editorialStyle.rowBorder,
                    expandableStyle.expandableHide,
                  ])}
                >
                  <th class={editorialStyle.sticky}>
                    <strong>USB A</strong>
                  </th>
                  <td>4x</td>
                  <td>2x</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>USB C</strong>
                  </th>
                  <td>2x</td>
                  <td>2x</td>
                </tr>
                <tr
                  class={mergeClasses([
                    editorialStyle.rowBorder,
                    expandableStyle.expandableHide,
                  ])}
                >
                  <th class={editorialStyle.sticky}>
                    <strong>Size (in)</strong>
                  </th>
                  <td>25 × 11.2 × 16.4"</td>
                  <td>27.3 × 13.4 × 16.2"</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>Size (mm)</strong>
                  </th>
                  <td>
                    <NoWrap>635 × 285 × 416 mm</NoWrap>
                  </td>
                  <td>
                    <NoWrap>693 × 341 × 410.4 mm</NoWrap>
                  </td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>Weight</strong>
                  </th>
                  <td>
                    <NoWrap>99lbs (45kg)</NoWrap>
                  </td>
                  <td>
                    <NoWrap>114lbs (51.5 kg)</NoWrap>
                  </td>
                </tr>
              </tbody>
            </table>
          </FixedTable>
          <p class="caption">
            <strong>1.</strong> Expected to increase to 1000W for each power
            station with the release of new{" "}
            <Link href={namedRoutes.ecoflow.altCharger.gen2}>
              gen 2 alternator chargers
            </Link>
            .
          </p>
        </Fragment>
        <br />

        <SlugHeading tag="h2">Full Specifications</SlugHeading>
        <Accordion
          headingsAs="h3"
          items={[
            {
              heading: "General",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    {/* <li>
                      <strong>Model:</strong> EFD521
                    </li> */}
                    <li>
                      <strong>Net Weight:</strong> 99lbs (45kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 25 × 11.2 × 16.4" (635 × 285
                      × 416 mm)
                    </li>
                    <li>
                      <strong>Warranty period:</strong> 24 months (5 years if
                      registered)
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "AC Power",
              content: (
                <Fragment>
                  <ul class="none" style="font-size:0.9em;">
                    <li>
                      AC Output:
                      <ul>
                        <li>
                          <strong>Wave Form:</strong> Pure sine wave
                        </li>
                        <li>
                          <strong>Output:</strong> 3,600W total (surge 7,200W)
                        </li>
                        <li>
                          <strong>Sockets (US/CA):</strong>
                          <ul class="circle">
                            <li>NEMA 5-20R (4×): 120V~20A(60Hz)</li>
                            <li>NEMA TT-30R (1×): 120V~30A(60Hz)</li>
                          </ul>
                        </li>
                      </ul>
                      <hr />
                    </li>
                    <li>
                      AC Input:
                      <ul>
                        <li>
                          <strong>AC Input Voltage:</strong> 100-120V~ 12.5A,
                          220-240V~ 12.5A, 50Hz/60Hz
                        </li>
                        <li>
                          <strong>AC Charge (US/CA):</strong> 120V~ 1,800W Max,
                          240V~ 3,000W Max
                        </li>
                      </ul>
                      <hr />
                    </li>
                  </ul>
                  <p class="caption">
                    See{" "}
                    <Link
                      external
                      href="https://manuals.ecoflow.com/us/product/delta-pro-portable-power-station?lang=en_US"
                    >
                      product manual
                    </Link>{" "}
                    for other countries.
                  </p>
                </Fragment>
              ),
            },
            {
              heading: "DC Power",
              content: (
                <Fragment>
                  <ul class="none" style="font-size:0.9em;">
                    <li>
                      DC Output:
                      <ul>
                        <li>
                          <strong>USB Output:</strong>
                          <ul class="circle">
                            <li>
                              USB-A (×2): 5V⎓2.4A, 12W Max per port, total 24W
                            </li>
                            <li>
                              USB-A Fast Charging (×2): 5V⎓2.4A 9V⎓2A 12V⎓1.5A,
                              18W Max per port, total 36W
                            </li>
                            <li>
                              USB-C (×2): 5/9/12/15/20V⎓5A 100W Max per port,
                              total 200W
                            </li>
                          </ul>
                        </li>
                        <li>
                          <strong>12V DC Output:</strong> 12.6V⎓30A, 378W total
                          <ul class="circle">
                            <li>
                              DC5521 Output (×2): 12.6V⎓3A, 38W Max per port
                            </li>
                            <li>
                              Anderson Powerpole port: 12.6V⎓30A, 378W Max
                            </li>
                          </ul>
                        </li>
                      </ul>
                      <hr />
                    </li>
                    <li>
                      DC Input:
                      <ul>
                        <li>
                          <strong>XT60 Port:</strong>
                          <ul class="none">
                            <li>Solar Charger: 11-150V⎓15A Max, 1,600W Max</li>
                            <li>
                              Car Input: Supports 12V/24V Battery, default 8A
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "Battery",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Rated Capacity:</strong> 3,600Wh, 48V⎓
                    </li>
                    <li>
                      <strong>Battery Chemistry:</strong> LFP
                    </li>
                    <li>
                      <strong>Cycle life:</strong> 3,500 cycles to 80% +
                      capacity
                    </li>
                    <li>
                      <strong>Protection Type:</strong> Over Voltage Protection,
                      Overload Protection, Over Temperature Protection, Short
                      Circuit Protection, Low Temperature Protection, Low
                      Voltage Protection, Overcurrent Protection
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "Environmental Temperature",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Optimal Operating Temperature:</strong> 68° to
                      86°F (20° to 30°C)
                    </li>
                    <li>
                      <strong>Discharge Temperature:</strong> 14° to 113°F (-10°
                      to 45°C)
                    </li>
                    <li>
                      <strong>Charge Temperature:</strong> 32° to 113°F (0° to
                      45°C)
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong> 14° to 113°F (-10°
                      to 45°C), optimal: 68° to 86°F (20° to 30°C)
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "Communications",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Connection:</strong> Wi-Fi, Bluetooth
                    </li>
                    <li>
                      <strong>CAN:</strong> REMOTE Port (RJ45)
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Accessories</SlugHeading>

        <SlugHeading tag="h3">DC Fuse Panel</SlugHeading>
        <p>
          Learn how to safely connect 12V accessories (fridge, heater, lights,
          etc.) to your EcoFlow using a DC fuse block:
        </p>
        <ReviewItem href={namedRoutes.ecoflow.wiring12v} />
        <Accordion
          right
          items={[
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Fuse block"
                  heading="Blue Sea Systems 5026 Fuse Block"
                  src="/images/products/blue-sea-systems/5026.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    There is also a 6 circuit model available (
                    <Link
                      affiliate="https://amzn.to/43tntjR"
                      href="https://www.amazon.com/dp/B000THQ0CQ"
                    >
                      5025
                    </Link>
                    ), but currently costs more and it's nice to have room to
                    add extra circuits in the future.
                  </p>
                  <p>
                    <strong>Model:</strong> 5026
                    <br />
                    <strong>Circuits:</strong> 12
                    <br />
                    <strong>Max Amperage:</strong> 30A per circuit (100A total)
                    <br />
                    <strong>Max Voltage:</strong> 32V DC
                    <br />
                    <strong>Fuse Type:</strong> ATO® or ATC®
                  </p>
                  <p>
                    You will also need to purchase the appropriate fuses to
                    match the wiring of your circuits. These can be purchased{" "}
                    <Link
                      affiliate="https://amzn.to/4oLUFvA"
                      href="https://www.amazon.com/dp/B00FLZRJJO"
                    >
                      individually
                    </Link>{" "}
                    or in a{" "}
                    <Link
                      affiliate="https://amzn.to/4r0jiGd"
                      href="https://www.amazon.com/dp/B00CQ8ILFA"
                    >
                      multi-pack
                    </Link>
                    . Be sure to use a reputable brand as these are used to
                    prevent an electrical fire.
                  </p>
                  <ButtonList>
                    <Button
                      affiliate="https://amzn.to/4qXtEXd"
                      href="https://www.amazon.com/dp/B001P6FTHC"
                      icon="Amazon"
                      secondary
                    >
                      Buy on Amazon
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Anderson to ring terminal"
                  heading="10AWG 45A"
                  src="/images/products/paekq-10awg-anderson-ring.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    Allows you to safely connect a DC fuse block to your power
                    station's Anderson Powerpole port.
                  </p>
                  <p>
                    <strong>Length:</strong> 25in
                    <br />
                    <strong>Gauge:</strong> 10AWG
                    <br />
                    <strong>Max Amperage:</strong> 45A (comes with 30A fuse)
                    <br />
                    <strong>Fuse Type:</strong> ATC®
                  </p>
                  <ButtonList>
                    <Button
                      affiliate="https://amzn.to/3JCH7TQ"
                      href="https://www.amazon.com/dp/B0B2P5JDX7"
                      icon="Amazon"
                      secondary
                    >
                      Buy on Amazon
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
          ]}
        />
        <br />

        <SlugHeading tag="h3">Alternator Chargers</SlugHeading>
        <p>
          More reliable than solar, these units pull power from your vehicle to
          quickly charge your power station as you drive.
        </p>
        <ul class="none">
          <li>
            <ReviewItem
              href={namedRoutes.ecoflow.altCharger.quick}
              description="500W & 800W Models"
            />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />
          </li>
        </ul>
        <br />

        <SlugHeading tag="h3">Solar Panels</SlugHeading>
        <p>
          EcoFlow offers both{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/collections/mountable-solar-panels"
          >
            mountable
          </Link>{" "}
          and{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/collections/portable-solar-panels"
          >
            portable
          </Link>{" "}
          solar panels, some of which are rigid, flexible, traditional, or
          bifacial. While EcoFlow provides various solar panel options and
          bundles, always prioritize panels that physically fit your vehicle;
          using other reputable brands is perfectly fine.
        </p>
        <br />

        <SlugHeading tag="h3">Extra Batteries</SlugHeading>
        <p>
          Supports two of the following extra batteries. Some of EcoFlow's
          alternator chargers use the same port which can limit you to a single
          extra battery. See full{" "}
          <Link
            href={`${namedRoutes.ecoflow.batteryCompatibility}?system=DELTA+Pro`}
          >
            battery compatibility details
          </Link>
          .
        </p>
        <PhotoListItem
          affiliateData={affiliateMerchants.ecoflow}
          href="https://us.ecoflow.com/products/delta-pro-smart-extra-battery?variant=40558805385289"
          img="/images/products/ecoflow/delta-pro-smart-extra-battery/front-right.webp"
          title="Delta Pro Smart Extra Battery"
          description={<Fragment>3600Wh extra battery</Fragment>}
          buttonText="EcoFlow"
          buttonIcon="EcoFlow"
        />

        <br />

        <SlugHeading tag="h3">Other Accessories</SlugHeading>
        <p>Remote control, solar cables, and adapters:</p>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-remote-control?variant=39428709351497"
              img="/images/products/ecoflow/accessories/remote-control.webp"
              title="Remote Control"
              description={<Fragment>Remote monitoring and control</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/collections/rv-ev-power-accessories/products/ev-x-stream-adapter-delta-pro?variant=39366327599177"
              img="/images/products/ecoflow/accessories/ev-x-stream-adapter-delta-pro.webp"
              title="EV X-Stream Adapter"
              description={
                <Fragment>
                  3400W charging via EV station (J1772 connector)
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>
        <Accordion
          items={[
            {
              heading: "View More",
              content: (
                <Fragment>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/collections/delta-3-series-accessories/products/solar-charging-cable?variant=39880496382025"
                        img="/images/products/ecoflow/accessories/solar-to-xt60-xt60i-charging-cable.webp"
                        title="Solar to XT60/XT60i Charging Cable"
                        description={
                          <Fragment>Charge from solar panels</Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/solar-extension-cable?_pos=1&_sid=e350c6fc3&_ss=r&variant=31865877823561"
                        img="/images/products/ecoflow/accessories/solar-extension-cable.webp"
                        title="Solar Extension Cable"
                        description={
                          <Fragment>Provides additional cable length</Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/solar-parallel-connection-cable?variant=31289813729353"
                        img="/images/products/ecoflow/accessories/solar-parallel-connection-cable.webp"
                        title="Solar Parallel Connection Cable"
                        description={
                          <Fragment>Connects solar panels in parallel</Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter?variant=39704596152393"
                        img="/images/products/ecoflow/accessories/smart-generator-adapter.webp"
                        title="Smart Generator Adapter"
                        description={
                          <Fragment>
                            For use with Alternator Charger or Smart Generator
                            (XT150 cables)
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/car-charging-cable?variant=39308519604297"
                        img="/images/products/ecoflow/accessories/car-charging-cable.webp"
                        title="Car Charging Cable"
                        titleSuffix="(included)"
                        description={
                          <Fragment>
                            Charge from your vehicle's cigarette lighter socket
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/collections/rv-ev-power-accessories/products/portable-power-station-grounding-adapter?variant=39987909984329"
                        img="/images/products/ecoflow/accessories/portable-power-station-grounding-adapter.webp"
                        title="Portable Power Station Grounding Adapter"
                        description={
                          <Fragment>
                            Neutral-ground bonding prevents floating ground
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Considerations</SlugHeading>
        <p>
          While the Delta Pro is a capable system, there are a few things worth
          noting if you’re considering it for your setup:
        </p>
        <ul>
          <li>
            <strong>Fan noise:</strong> The cooling fans can become loud under
            heavy loads or when charging via AC power.
          </li>
          <li>
            <strong>UPS switchover speed:</strong> The unit has a 30ms
            switchover time, which may not be fast enough for sensitive
            equipment like desktop computers.
          </li>
          <li>
            <strong>DC Output:</strong> The 30A DC output is quite good and
            sufficient for most vanlife and off‑grid needs. However, those with
            unusually high DC demands may still need to plan around this limit.
          </li>
          <li>
            <strong>Cold weather use:</strong> The Delta Pro does not include
            self‑heating, so performance can be restricted if internal
            temperatures drop below 14°F.
          </li>
          <li>
            <strong>Solar startup clicking:</strong> When solar charging begins
            in the morning with low light, the internal relay may click on and
            off until there’s enough sunlight for stable charging. It’s expected
            behavior, though worth noting if you’re sensitive to small noises
            while sleeping.
          </li>
          <li>
            <strong>Port placement:</strong> Many of the ports are located on
            the sides of the unit. This can make cables more prone to being
            bumped or disconnected, especially smaller DC cables, so plan your
            installation accordingly.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">My Verdict</SlugHeading>
        <p>
          The EcoFlow Delta Pro remains a highly capable power station that
          balances performance, expandability, and value. It’s packed with
          features that make it suitable for serious vanlife builds or home
          backup setups, and even though newer versions add refinements, the
          original Delta Pro still holds its own as a dependable all‑in‑one
          solution. If you’re looking for a proven system at a lower price point
          than the latest generation, the Delta Pro is tough to beat.
        </p>

        <EcoFlowCouponCode
          href="https://us.ecoflow.com/collections/delta-pro-series/products/delta-pro-portable-power-station"
          buttonText="Buy from EcoFlow"
        />

        <p>
          Not sure it’s the best fit? My{" "}
          <Link href={namedRoutes.powerSystems}>power systems guide</Link>{" "}
          covers all the popular brands.
        </p>

        <ButtonList>
          <Button
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/collections/delta-pro-series/products/delta-pro-portable-power-station"
            icon="EcoFlow"
          >
            Buy from EcoFlow
          </Button>
          <Button
            affiliate="https://amzn.to/4oJeF0L"
            href="https://www.amazon.com/dp/B0C1Z4GLKS/"
            secondary
            icon="Amazon"
          >
            Buy on Amazon
          </Button>
        </ButtonList>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.dp3} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.d3ultraPlus} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="EcoFlow DELTA Pro"
        description={
          <Fragment>
            $1,499 <s>$3,699</s> | 3,600Wh
          </Fragment>
        }
      >
        <Button
          affiliateData={affiliateMerchants.ecoflow}
          href="https://us.ecoflow.com/collections/delta-pro-series/products/delta-pro-portable-power-station"
          icon="EcoFlow"
        >
          View on EcoFlow
        </Button>
        <Button
          affiliate="https://amzn.to/4oJeF0L"
          href="https://www.amazon.com/dp/B0C1Z4GLKS/"
          secondary
          icon="Amazon"
        >
          View on Amazon
        </Button>
      </StickyButtons>
    </Editorial>
  </div>
);

export default Page;
